import { useMutation, useQuery } from '@tanstack/react-query';
import { cloneDeep } from 'lodash';

declare global {
  interface Window {
    structuredClone<T>(value: T): T;
  }
  function structuredClone<T>(value: T): T;
}
import backendService from '../services/backendService';
import BackendService, {
  ConfirmationState,
} from '../services/backendService/backendService';
import useRouterMatch from './useRouterMatch';
export const accessBadgeCacheKey = 'accessBadge';

export enum LicenseCoarseLevels {
  enterprise = 'Enterprise',
  premium = 'Premium',
  freemium = 'Freemium',
}

export enum LicenseFineLevels {
  trial = 'Trial',
  full = 'Full',
}

export enum OrganizationType {
  School = 'School',
  District = 'District',
  Other = 'Other',
}

export enum OrganizationSource {
  MCH = 'MCH',
  Curipod = 'Curipod',
  User = 'User',
}

export type SimplifiedUser = {
  id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email: string;
  preferredLocale?: string;
  profileInfo?: {
    role?: string;
    subjects?: string[];
    grades?: string[];
    intendedUses?: string[];
    otherIntendedUse?: string;
    signUpOrigin?: string;
    otherSignUpOrigin?: string;
  };
  createdAt: number;
  onboardingCompletedAt?: number;
};

export type SimplifiedLicense = {
  id?: string;
  source?:
    | 'EnterpriseOnboardingStarted'
    | 'PaidPilot'
    | 'FreePilot'
    | 'AmbassadorAccess'
    | 'OneOffExtension'
    | 'SignUp'
    | 'PremiumDiscontinued';
  coarseLevel: LicenseCoarseLevels;
  fineLevel: LicenseFineLevels;
  aiAccessBlocked: boolean;
  expiresAt?: number;
  createdAt: number;
};

export type SimplifiedOrganization = {
  id: string;
  type: OrganizationType;
  source: OrganizationSource;
  name: string;
  primaryZipCode?: string;
  secondaryZipCode?: string;
  city?: string;
  region?: string;
  country?: string;
  ssoProvider?: 'Google' | 'Clever' | 'Classlink' | 'Feide';
  parent?: SimplifiedOrganization;
  createdAt: number;
  reconfirmAt: Date | null;
  // Schools that follow an English curriculum (US/UK/IB international
  // schools in non-native-English countries). Set via the international-
  // school modal during onboarding or from the home page. Tri-state:
  //   true       → teacher explicitly said "yes, intl school"; treat as
  //                a US teacher (full rank-based card library, English
  //                content forced).
  //   false      → teacher explicitly said "no, local school"; keep the
  //                curated international experience.
  //   undefined  → never asked (field absent on the org record).
  //   null       → never asked (SQL NULL serialised by the backend).
  // Consumers must distinguish the three with `=== true` / `=== false` /
  // `== null` rather than truthy / falsy collapses, otherwise an
  // explicit "no" silently looks identical to "never asked".
  isInternational?: boolean | null;
  // Org-level student sign-in policy, mirrored onto the badge so every surface
  // can read it. The authoritative copy lives behind
  // `GET /v0.5/organizations/:orgId/settings`, which requires an Admin
  // TenantUser — reading it from here is what lets a regular teacher see the
  // policy at all.
  //
  // Documented as always present and never partial. Read it with `?.` anyway:
  // this field is newer than the clients that consume it, so a badge served by
  // an older backend has no `settings` at all, and the safe reading of "we don't
  // know" is the same as "off".
  settings: OrganizationStudentSsoSettings;
};

// An org the user belongs to, with their own role in it (their TenantUser row) —
// the role every org-scoped endpoint checks. Gate managing the org with canManageOrg
// (utils/canManageOrg), which falls back for badges without it. Not on
// `parent`: the user may have no row in the district.
export type OrganizationWithRole = SimplifiedOrganization & { role: 'Admin' | 'Member' };

// Extracted rather than inlined so tests can type their badge mocks against the
// same shape the components read.
//
// Deliberately NOT `OrganizationSsoSettings` from services/backendService/types:
// that one feeds `UpdateSsoSettingsInput = Partial<…>`, so putting a derived,
// read-only field like `isStudentSsoEnabled` in it would typecheck as patchable
// at every saveSettings() call.
export type OrganizationStudentSsoSettings = {
  // CAPABILITY: at least one of the org's SSO providers has `allowStudents`, so
  // students are able to sign in at all. Derived server-side from the provider
  // rows — the same predicate the admin tab computes as `hasStudentProviders`
  // from `allowedStudentSsoProviders`, which is Admin-only and so unreadable by
  // a regular teacher. This is what decides whether a teacher is offered the
  // per-lesson toggle, and it is independent of the default below.
  isStudentSsoEnabled: boolean;
  // The org's default join method: true means new lessons require students to
  // sign in with a provider. Snapshotted into each lesson at creation.
  //
  // No client reads this, deliberately. The lesson's own stored value is the
  // only truth: OR-ing the default into the switch would show ON for a lesson
  // created before the org flipped it, and clicking off would dispatch `false`
  // onto an already-false setting, so the switch would snap back and never clear.
  requireStudentSsoDefault: boolean;
  // Checked at join time for every lesson, existing ones included, so a
  // teacher cannot opt a single lesson out.
  enforceStudentSsoForAllLessons: boolean;
};

// Kept for legacy purposes. Will be deprecated in the near future.
export type LegacyTenant = {
  tenantId: string;
  tenantName: string;
  organisationId?: string | null;
  isEnterprise?: boolean | null; // Deprecated - used by pods enterprise - dont migrate data before pod is 100% shutoff
  districtId?: string | null;
  districtName?: string | null;
  state?: string | null;
  country?: string | null;
  createdAt?: number | null;
  createdBy?: string | null;
  isPremium?: boolean | null; // Deprecated - used by pods - dont migrate data before pod is 100% shutoff
  isCuripodPremium?: boolean | null;
  isCuripodEnterprise?: boolean | null;
  curipodTrialExpirationDate?: number;
  curipodTrialReason?: string;
  curipodEnterpriseExpirationDate?: number;
};

export type RawUserAccessBadge = {
  role: 'Admin' | 'Member';
  user: SimplifiedUser;
  license?: SimplifiedLicense;
  organization?: OrganizationWithRole;
  otherOrganizations?: OrganizationWithRole[];
  tenant: LegacyTenant;
  canPurchaseIndividualPremium: boolean;
};

function deduplicateOrganizations<T extends { id: string }>(
  orgs: T[] | undefined,
  excludeId?: string,
): T[] | undefined {
  if (!orgs) return undefined;
  const seen = new Set<string>();
  if (excludeId) seen.add(excludeId);
  return orgs.filter(org => {
    if (seen.has(org.id)) return false;
    seen.add(org.id);
    return true;
  });
}

export class UserAccessBadge {
  // Admin here if Admin of this org or of its district. For display; whether the
  // user may manage the org is canManageOrg (utils/canManageOrg).
  readonly role: 'Admin' | 'Member';
  readonly user: Readonly<SimplifiedUser>;
  readonly license: Readonly<SimplifiedLicense>;
  readonly organization?: Readonly<OrganizationWithRole>;
  readonly otherOrganizations?: Readonly<OrganizationWithRole[]>;
  readonly tenant: Readonly<LegacyTenant>;
  readonly canPurchaseIndividualPremium: boolean;

  constructor(
    role: 'Admin' | 'Member',
    user: SimplifiedUser,
    license: SimplifiedLicense | undefined,
    organization: OrganizationWithRole | undefined,
    otherOrganizations: OrganizationWithRole[] | undefined,
    tenant: LegacyTenant,
    canPurchaseIndividualPremium: boolean,
  ) {
    this.role = role;
    this.user = Object.freeze(cloneDeep(user));
    this.license = license
      ? Object.freeze(cloneDeep(license))
      : {
          coarseLevel: LicenseCoarseLevels.freemium,
          fineLevel: LicenseFineLevels.full,
          aiAccessBlocked: false,
          createdAt: new Date().getTime(),
        };
    this.organization = Object.freeze(cloneDeep(organization));
    this.otherOrganizations = Object.freeze(
      cloneDeep(deduplicateOrganizations(otherOrganizations, organization?.id)),
    );
    this.tenant = Object.freeze(cloneDeep(tenant));
    this.canPurchaseIndividualPremium = canPurchaseIndividualPremium;
    Object.freeze(this);
  }
}

function useAccessBadge() {
  const { tenantId: activeTenantId } = useRouterMatch();
  const { data, isFetching, isFetched, refetch: refetchAccessBadge } = useQuery({
    queryKey: [accessBadgeCacheKey, activeTenantId],

    queryFn: _ => {
      if (!activeTenantId) return undefined;
      return backendService.instance.users.getAccessBadge(activeTenantId);
    },

    staleTime: 1000 * 60 * 60,
    enabled: !!activeTenantId,
  });

  const { mutate, isSuccess, isPending: isLoading } = useMutation({
    mutationFn: async ({
      schoolId,
      schoolCountry,
    }: {
      schoolId?: number;
      schoolCountry?: string;
      onSuccessFunction?: () => void;
    }) => {
      return backendService.instance.users.updateSchoolInfo({
        activeTenantId,
        schoolId,
        schoolCountry,
      });
    },

    onSuccess: (_, { onSuccessFunction }) => {
      if (onSuccessFunction) onSuccessFunction();
    },
  });

  const {
    mutate: setAssignmentConfirmation,
    isSuccess: isConfirmAssignmentSuccess,
    isPending: isConfirmAssignmentLoading,
  } = useMutation({
    mutationFn: async ({
      confirmationState,
      durationDays,
    }: {
      confirmationState: ConfirmationState;
      durationDays?: number;
      onSuccessFunction?: () => void;
    }) => {
      return backendService.instance.users.setAssignmentConfirmation(
        confirmationState,
        durationDays,
      );
    },

    onSuccess: (_, { onSuccessFunction }) => {
      if (onSuccessFunction) onSuccessFunction();
    },
  });

  const {
    mutate: reselectOrganization,
    isSuccess: isReselectOrganizationSuccess,
    isPending: isReselectOrganizationLoading,
  } = useMutation({
    mutationFn: async (params: {
      newOrgId?: string;
      customOrgName?: string;
      city?: string;
      region?: string;
      country?: string;
      primaryZipCode?: string;
      organizationType?: OrganizationType;
      onSuccessFunction?: () => void;
    }) => {
      const { onSuccessFunction: _onSuccessFunction, ...orgParams } = params;
      return backendService.instance.organizations.reselectOrganization(orgParams);
    },

    onSuccess: (_, { onSuccessFunction }) => {
      if (onSuccessFunction) onSuccessFunction();
    },
  });

  const { mutate: switchOrganization } = useMutation({
    mutationFn: (organizationId: string) =>
      BackendService.instance.switchOrganization(organizationId),

    onSuccess: () => {
      window.open(`${window.location.origin}`, '_self');
    },
  });

  return {
    accessBadge: data,
    isFetching,
    isFetched,
    refetchAccessBadge,
    // These are for the old school info update
    updateSchoolInfo: mutate,
    isUpdatingSchoolInfo: isLoading,
    isSuccess: isSuccess,
    // These are for the new assignment confirmation
    setAssignmentConfirmation,
    isConfirmAssignmentSuccess,
    isConfirmAssignmentLoading,
    // These are for the new organization reselection
    reselectOrganization,
    isReselectOrganizationSuccess,
    isReselectOrganizationLoading,
    switchOrganization,
  };
}

export default useAccessBadge;
